import { useEffect, useRef } from 'react';
import type { PlannedPoint, PlanResponse, RidingPreferences } from '../../shared/contracts.js';
import { formatDistance, formatDuration } from '../../shared/format.js';
import { MapCanvas } from './MapCanvas.js';
import { RouteResultCard } from './RouteResultCard.js';
import { TourSummary } from './TourSummary.js';

/** One sub-trip of the trip being shared. */
export interface OverviewSubtrip {
  plan: PlanResponse;
  points: PlannedPoint[];
}

/**
 * A clean, screenshot-friendly view of the planned trip: the route map beside
 * the result card's headline figures. A trip with several sub-trips shows every
 * route on the map and each sub-trip's figures, with the whole trip's total.
 * Sharing is the rider's own screenshot; nothing is sent anywhere.
 */
export function TripOverview({ tripName, subtrips, selected, requested, onClose }: {
  tripName: string;
  subtrips: OverviewSubtrip[];
  /** The open sub-trip, whose points and card a single-sub-trip overview shows. */
  selected: number;
  requested: RidingPreferences;
  onClose: () => void;
}) {
  const closeButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    closeButton.current?.focus();
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const several = subtrips.length > 1;
  const open = subtrips[Math.min(selected, subtrips.length - 1)]!;
  return (
    <div className="overview-backdrop" role="dialog" aria-modal="true" aria-label={`${tripName} overview`}>
      <button ref={closeButton} type="button" className="overview-close" onClick={onClose}>Close <kbd>Esc</kbd></button>
      <article className="overview-card">
        <div className="overview-map">
          <MapCanvas points={several ? [] : open.points} routes={subtrips.map(({ plan }) => ({ route: plan.route }))} overview />
        </div>
        {several ? (
          <div className="overview-result tour-overview">
            <p className="eyebrow">{tripName}</p>
            <TourSummary entries={subtrips} className="tour-total tour-total-large" />
            <ol className="tour-list">
              {subtrips.map(({ plan }, index) => (
                <li key={plan.planId}><span>Sub-trip {index + 1}</span><strong>{formatDistance(plan.route.distanceMeters)} · {formatDuration(plan.route.timeSeconds)}</strong></li>
              ))}
            </ol>
          </div>
        ) : <RouteResultCard plan={open.plan} requested={requested} eyebrow={tripName} compact className="overview-result" />}
      </article>
    </div>
  );
}
